import { ActionReducer, createFeature, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import { APPLICATION_ACTIONS, CONVERSATION_IDS_API_ACTIONS, IDataQueryParam } from '../actions';
import { ApiState, IApiState } from '../../shared/models/api-state.model';
import { IConversationIDsAPIResponseNormalized } from '../../shared/models/conversation-ids.model';
import { EScreenSizeBreakPoint, EViewMode } from '../../core/configurations/application';

export const APPLICATION_STATE_KEY = 'application';

export interface IApplicationState {
  loading: boolean;
  dataQueryParam: IDataQueryParam;
  conversationIDs: IApiState<IConversationIDsAPIResponseNormalized>;
  screenSizeBreakPoint: EScreenSizeBreakPoint;
  viewMode: EViewMode | null;
}

export const initialState: IApplicationState = {
  loading: true,
  dataQueryParam: {} as IDataQueryParam,
  conversationIDs: new ApiState(),
  screenSizeBreakPoint: EScreenSizeBreakPoint.lg,
  viewMode: null,
};

const reducer: ActionReducer<IApplicationState> = createReducer<IApplicationState>(
  initialState,
  on(APPLICATION_ACTIONS.showLoader, (state): IApplicationState => ({ ...state, loading: true })),
  on(APPLICATION_ACTIONS.hideLoader, (state): IApplicationState => ({ ...state, loading: false })),
  on(
    APPLICATION_ACTIONS.setDataQueryParam,
    (state, { queryName, value }): IApplicationState => ({ ...state, dataQueryParam: { queryName, value } }),
  ),
  on(
    CONVERSATION_IDS_API_ACTIONS.get,
    (state): IApplicationState => ({
      ...state,
      conversationIDs: state.conversationIDs.load(),
    }),
  ),
  on(
    CONVERSATION_IDS_API_ACTIONS.error,
    (state, { error }): IApplicationState => ({
      ...state,
      conversationIDs: state.conversationIDs.setError(error),
    }),
  ),
  on(
    CONVERSATION_IDS_API_ACTIONS.success,
    (state, { data }): IApplicationState => ({
      ...state,
      conversationIDs: state.conversationIDs.success(data),
    }),
  ),
  on(
    APPLICATION_ACTIONS.setScreenSizeBreakpoint,
    (state, { breakpoint }): IApplicationState => ({
      ...state,
      screenSizeBreakPoint: breakpoint,
    }),
  ),
  on(
    APPLICATION_ACTIONS.setViewMode,
    (state, { viewMode }): IApplicationState => ({
      ...state,
      viewMode,
    }),
  ),
);

export const APPLICATION_REDUCER = createFeature({
  name: APPLICATION_STATE_KEY,
  reducer,
  extraSelectors: ({ selectScreenSizeBreakPoint, selectViewMode, selectConversationIDs }) => ({
    isScreenSizeBreakpoint: (size: EScreenSizeBreakPoint): MemoizedSelector<any, boolean> =>
      createSelector(selectScreenSizeBreakPoint, (currentBreakpointSize): boolean => size === currentBreakpointSize),
    isViewMode: (mode: EViewMode): MemoizedSelector<any, boolean> =>
      createSelector(selectViewMode, (viewMode): boolean => mode === viewMode),
    selectCallDetailId: createSelector(selectConversationIDs, ({ data }) => data?.callDetailId ?? 0),
  }),
});
